import type { SourceFieldInfo } from './SourceFieldInfo.types';

/**
 * Infers the data type of a value by analyzing its content and format
 * Used for validating field mappings between API data and table columns
 * @param value - The raw value to analyze
 * @param formattedValue - Optional formatted version of the value to check
 * @returns The inferred type (text, currency, percentage, number, date, boolean, or object)
 */
export const inferType = (value: any, formattedValue?: any): SourceFieldInfo['inferredType'] => {
  if (value === null || value === undefined) {
    return 'text';
  }

  if (typeof value === 'boolean') {
    return 'boolean';
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return 'object';
  }

  const checkValue = formattedValue !== undefined ? String(formattedValue) : String(value);

  if (checkValue.includes('$') || checkValue.includes('€') || checkValue.includes('£')) {
    return 'currency';
  }

  if (checkValue.includes('%')) {
    return 'percentage';
  }

  const datePatterns = [
    /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
    /\b\d{4}-\d{2}-\d{2}\b/,
    /\b\d{1,2}-\d{1,2}-\d{4}\b/,
  ];
  if (datePatterns.some(pattern => pattern.test(checkValue))) {
    return 'date';
  }

  if (typeof value === 'number') {
    return 'number';
  }

  const trimmed = checkValue.trim();
  
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return 'number';
  }
  
  const digitsOnly = trimmed.replace(/[^0-9]/g, '');
  const totalLength = trimmed.length;
  
  if (digitsOnly.length >= 4 && digitsOnly.length / totalLength >= 0.7 && !trimmed.includes(' ')) {
    return 'number';
  }

  return 'text';
};

